import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * StatStrip, the borderless peek-scroll row of stat chips.
 *
 * CSS selector: `mobile/molecules.css` `.ds-statstrip`, with
 * `.ds-statstrip__chip`, `__value`, `__trend` (+ `--up`, `--down`) and
 * `__label`.
 *
 * Doctrine: borderless, tone-lift depth and hairline dividers only. The strip
 * bleeds to the screen edge so the next chip peeks, which is what tells the
 * user it scrolls, and the scrollbar is hidden.
 *
 * The bleed distance is the screen's own side padding and the CSS reads it from
 * `--ds-bleed`. Set that variable in the screen's stylesheet or pass it through
 * `style` at the call site. It is a spacing value, not a themeable one, so no
 * component here hardcodes it.
 *
 * There is no `--first` or `--last` chip modifier and none is invented. The CSS
 * drops the left padding on `:first-child` and the divider on `:last-child`, so
 * render chips as direct siblings.
 */
export const StatStrip = React.forwardRef(function StatStrip({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('ds-statstrip', className)} {...rest} />;
});

const TREND_DIRECTION = {
  up: 'ds-statstrip__trend--up',
  down: 'ds-statstrip__trend--down',
};

/**
 * StatChip, one figure and its caption inside a `<StatStrip>`.
 * CSS selector: `mobile/molecules.css` `.ds-statstrip__chip`.
 * `trend` is `{ direction, label }` and rides on the same baseline as the value.
 */
export const StatChip = React.forwardRef(function StatChip(
  { className, value, label, trend, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('ds-statstrip__chip', className)} {...rest}>
      <p className="ds-statstrip__value">
        {value}
        {trend ? (
          <span className={cn('ds-statstrip__trend', TREND_DIRECTION[trend.direction])}>
            {trend.label}
          </span>
        ) : null}
      </p>
      <p className="ds-statstrip__label">{label}</p>
    </div>
  );
});

export default StatStrip;
